import React, { useState, useEffect, useMemo } from "react";
import { useMutation } from "react-query";

import { AppointmentsAvailability } from "@secondcloset/logistics-utils";
import { Fulfillment } from "@secondcloset/types";
import { DateTimeSelector } from "@secondcloset/web-components";

import { Modal, notification, Spin } from "antd";
import { startCase } from "lodash-es";
import moment from "moment";

// lib
import { fetchShipmentAvailabilities, recreateShipment } from "../../../../../../api/fulfillment/shipment";
import { isSupportEscalated } from "../../../../../../helperFunctions/user";
import useUser from "../../../../../../hooks/reduxContainer/_common/useUser";
import Shipment from "../../../../../../lib/fulfillment/shipment";
import Organization from "../../../../../../lib/organization";

type ShipmentType = Fulfillment.Shipment;
type PackageType = Fulfillment.Package;
type Order = Fulfillment.Order;
type SecondClosetShippingMethod = Fulfillment.SecondClosetShippingMethod;

interface Props {
  visible: boolean;
  setVisible: (visible: boolean) => any;
  shipment: ShipmentType;
  order: Order;
  onRefetch: () => any;
  // when clicking return to sender button, different from recreating a return to sender shipment
  isNewReturnToSender?: boolean;
  returnToSenderAddressID?: string;
}

const RecreateShipmentModal: React.FC<Props> = ({
  visible,
  setVisible,
  shipment,
  order,
  onRefetch,
  isNewReturnToSender,
  returnToSenderAddressID,
}) => {
  const [selectedDate, setSelectedDate] = useState<string>();
  const [selectedTime, setSelectedTime] = useState<string>();
  const { mutateAsync: doRecreateShipment, ...shipmentDetails } = useMutation(recreateShipment);
  const { mutate: doFetchShipmentAvailabilities, ...shipmentAvailabilities } = useMutation(fetchShipmentAvailabilities);

  const { user } = useUser();

  const isIkea = Organization.isIKEA(order?.organization);

  const role = user?.role || null;
  const subrole = user?.subrole || null;

  // when it is ikea, all user will have admin ability to reschedule
  const isAdmin = isSupportEscalated(role, subrole) || isIkea;

  useEffect(() => {
    if (!visible) return;
    const today = moment().format("YYYY-MM-DD");
    const daysAhead = moment().add(90, "days").format("YYYY-MM-DD");
    const options = {
      shipment_id: shipment?.id,
      date_from: today,
      date_to: daysAhead,
      organization_id: order?.organization?.id,
    };
    doFetchShipmentAvailabilities(options);
    // eslint-disable-next-line
  }, [visible]);

  const isSecondClosetAppointment = shipment?.shipping_method_type === "appointment";
  const shippingMethod = shipment?.shipping_method as SecondClosetShippingMethod;
  const deliveryType = (shipment?.shipping_method as Fulfillment.SecondClosetShippingMethod)?.delivery_type;
  const availabilityInstance = useMemo(() => {
    return new AppointmentsAvailability(shipmentAvailabilities.data || [], {
      isAdmin,
      daysTillNextAvailableBooking: deliveryType === "parcel" ? 0 : 2,
    });
    // eslint-disable-next-line
  }, [shipmentAvailabilities.data]);

  if (!isSecondClosetAppointment) return null;

  const jobType = isNewReturnToSender ? "return_to_sender" : shippingMethod?.job_type;

  const modalTitle = `Re-create ${startCase(jobType)} Shipment`;

  const onCreateShipment = async () => {
    if (!selectedTime || !selectedDate) {
      return notification.error({ message: "Please select date and time" });
    }
    const body = {
      order_id: order.id,
      shipment_id: shipment.id,
      date: selectedDate,
      time_range: selectedTime,
    };
    const result = await doRecreateShipment(body);

    if (result) {
      notification.success({
        message: "Success!",
        description: `New ${startCase(jobType)} shipment created`,
      });
      onRefetch();
    }

    setVisible(false);
  };

  const isDateDisabled = (date: string) => {
    return availabilityInstance.isDateOneDisabled(date);
  };

  const isSlotDisabled = (timeslot: string) => {
    return availabilityInstance.isSlotOneDisabled(selectedDate || "", timeslot);
  };

  const renderDateTimeSelector = () => {
    return (
      <div className="date-picker">
        <DateTimeSelector
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          onSelectDate={(date: string | null) => setSelectedDate(date || undefined)}
          onSelectTime={setSelectedTime}
          disabledDate={isDateDisabled}
          disabledTime={(slot) => {
            if (isAdmin) return false;
            return isSlotDisabled(slot || "");
          }}
          slots={availabilityInstance.getTimeslots(selectedDate || null)}
        />
      </div>
    );
  };

  return (
    <Modal
      visible={visible}
      onCancel={() => setVisible(false)}
      title={modalTitle}
      onOk={onCreateShipment}
      width={600}
      confirmLoading={shipmentDetails.isLoading}
    >
      <Spin tip="Fetching availability..." spinning={shipmentAvailabilities.isLoading}>
        {renderDateTimeSelector()}
      </Spin>
    </Modal>
  );
};

export default RecreateShipmentModal;
